<template>
  <Form
    :form-id="formId"
    :initial-values="initialValues"
    action="#"
    :class="{ dirty }"
  >
    <HiddenInput input-id="id" />

    <VPInput
      ref="nameRef"
      :disabled="disabled"
      input-id="name"
      placeholder="Add ticket"
    />
    <VPInput
      ref="sectionRef"
      :disabled="disabled"
      input-id="section"
      placeholder="Add section"
    />
    <VPPriceInput
      ref="priceRef"
      :disabled="disabled"
      input-id="price"
      placeholder="Add price"
      hide-empty-input
    />
    <VPInput
      ref="projectedSoldRef"
      type="integer"
      :disabled="disabled"
      input-id="projected"
      placeholder="Add number"
      class="text-right"
    />
    <div class="right-field overflow-x-auto">
      {{ totalDisplay }}
    </div>
  </Form>
</template>

<script setup lang="ts">
import { Form, HiddenInput, useFormsStore } from 'vuniform';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { formatCurrency } from '@/helpers/currency';
import VPPriceInput from '@/components/form/VPPriceInput.vue';
import VPInput from '@/components/form/VPInput.vue';

const nameRef = ref<VPInput>(null);
const sectionRef = ref<VPInput>(null);
const priceRef = ref<VPPriceInput>(null);
const projectedSoldRef = ref<VPInput>(null);

const formsStore = useFormsStore();

interface Props {
  formId: string;
  initialValues: unknown;
  total: number;
  disabled?: boolean;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'blur'): void;
  (e: 'save'): void;
  (e: 'clear'): void;
}>();

const totalDisplay = computed(() => {
  if (!formsStore.fieldGetValue(props.formId, 'id')) {
    return '';
  }

  const total = isNaN(props.total) ? 0 : props.total;
  return formatCurrency(total, { onZero: '-' });
});

const refs = [nameRef, sectionRef, priceRef, projectedSoldRef];

const activeElement = ref<EventTarget | null>(null);

const focusChanged = (e: Event) => {
  activeElement.value = e.target;
};

onMounted(() => {
  document.addEventListener('focusin', focusChanged);
});
onBeforeUnmount(() => {
  document.removeEventListener('focusin', focusChanged);
});

const hasFocus = computed(() =>
  refs.some(r => r.value?.inputRef && activeElement.value && r.value?.inputRef === activeElement.value),
);

const hasValue = computed(() => {
  return ['name', 'section', 'price', 'projected'].some(f => formsStore.fieldGetValue(props.formId, f));
});

watch(hasFocus, (newValue, oldValue) => {
  if (oldValue && !newValue) {
    if (hasValue.value) {
      emit('blur');
    } else {
      emit('clear');
    }
  }
});

const dirty = computed(() => formsStore.isFormDirty(props.formId));
</script>
